import React, { useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { apiClient } from "../lib/api";
import { toast } from "sonner";

/**
 * "Notify me when available" — captures the visitor's mobile when the product
 * is out of stock and stores it server-side (stock_alerts collection).
 * `variant` toggles the button styling: "primary" (full CTA) | "compact" (sticky bar).
 */
const NotifyMeButton = ({ variant = "primary", label = "Notify me when available" }) => {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (mobile.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/stock-alerts", { mobile, name });
      toast.success(
        "Thanks! We'll ping you as soon as it's back in stock."
      );
      setOpen(false);
      setMobile("");
      setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your number");
    } finally {
      setSubmitting(false);
    }
  };

  const triggerClass =
    variant === "compact"
      ? "cta-red rounded-full px-5 sm:px-7 h-12 inline-flex items-center gap-1.5 font-bold text-sm sm:text-base shrink-0"
      : "cta-red rounded-full h-14 px-7 text-base font-semibold flex-1 inline-flex items-center justify-center gap-2";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`notify-me-${variant}`}
          className={triggerClass}
        >
          <BellRing className="w-4 h-4" />
          {label}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Notify me when available</DialogTitle>
          <DialogDescription>
            Drop your mobile number and we&apos;ll message you as soon as
            stock is back. No spam — just this one alert.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-[#2C3E50]">
              Your name (optional)
            </label>
            <Input
              data-testid="notify-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Priya Sharma"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2C3E50]">
              Mobile number
            </label>
            <Input
              data-testid="notify-mobile-input"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/[^\d+ ]/g, ""))}
              placeholder="10-digit mobile"
              className="mt-1 h-11"
              inputMode="numeric"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            data-testid="notify-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            data-testid="notify-submit-btn"
            onClick={submit}
            disabled={submitting}
            className="cta-red rounded-full"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Notify me
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotifyMeButton;
